/**
 * PrimaryNav — the responsive top-bar navigation (nav rework 2026-07-13).
 *
 * Replaces both the fixed desktop pill row and the mobile bottom tab bar with
 * ONE priority + overflow bar. The gated nav items render as pills in
 * registry-priority order; when they don't all fit the available width, the
 * lowest-priority tail collapses into a "More" dropdown. At mobile widths the
 * bar fits ~zero pills, so "More" naturally becomes the full menu of every
 * item — the founder-requested behavior, from one mechanism.
 *
 * Width is measured live (ResizeObserver over the nav + a hidden natural-width
 * row); the split math is the pure `computeVisibleCount` / `splitNavLinks` in
 * navOverflow.ts. When no layout is available (SSR / jsdom, where every
 * measured width is 0) the bar shows every item — the safe default, matching
 * useIsMobile's "assume desktop when unmeasurable" stance.
 *
 * Gating: the caller passes the ALREADY-GATED list (visibleNavLinks(whoami)),
 * so role-hidden items never appear — in the bar OR in the "More" menu.
 *
 * House rule (React #310): every hook runs before any conditional return.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { computeVisibleCount, splitNavLinks } from '@/lib/navOverflow';
import type { NavEntry, NavSection } from '@/lib/navSection';

/** Inter-item gap (px) — mirrors the `gap-1` (0.25rem) between pills. */
const GAP_PX = 4;

export interface PrimaryNavProps {
  /** Already capability-gated, in registry (priority) order. */
  links: readonly NavEntry[];
  tenantSlug: string;
  activeSection: NavSection | null;
}

function pillClass(isActive: boolean): string {
  return (
    'inline-flex items-center gap-[7px] h-[38px] px-[15px] rounded-pill text-sm whitespace-nowrap ' +
    'transition-colors duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
    (isActive
      ? 'text-sage-800 bg-paper shadow-sh1 font-semibold'
      : 'text-ink-soft font-medium hover:text-sage-800 hover:bg-sage-800/[6%]')
  );
}

export function PrimaryNav({ links, tenantSlug, activeSection }: PrimaryNavProps): ReactNode {
  const navRef = useRef<HTMLElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const moreMeasureRef = useRef<HTMLSpanElement | null>(null);
  const moreRef = useRef<HTMLDivElement | null>(null);

  // Default to "show all" until measured (the unmeasurable / no-layout path).
  const [visibleCount, setVisibleCount] = useState(links.length);
  const [moreOpen, setMoreOpen] = useState(false);

  const recompute = useCallback(() => {
    const nav = navRef.current;
    const measure = measureRef.current;
    if (!nav || !measure) return;
    const available = nav.clientWidth;
    // No layout (SSR / jsdom): every width reads 0. Don't collapse — show all.
    if (available === 0) {
      setVisibleCount(links.length);
      return;
    }
    const itemEls = Array.from(
      measure.querySelectorAll<HTMLElement>('[data-measure-item]'),
    );
    const widths = itemEls.map((el) => el.offsetWidth + GAP_PX);
    const moreWidth = (moreMeasureRef.current?.offsetWidth ?? 0) + GAP_PX;
    setVisibleCount(computeVisibleCount(widths, available, moreWidth));
  }, [links]);

  // Measure on mount + on every resize. recompute() runs synchronously here so
  // the split is correct on first paint even where ResizeObserver is absent.
  useLayoutEffect(() => {
    recompute();
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => recompute());
    ro.observe(nav);
    return () => ro.disconnect();
  }, [recompute]);

  // Close the More menu on click-outside + Escape.
  useEffect(() => {
    if (!moreOpen) return;
    function onClick(e: MouseEvent): void {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setMoreOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  const { visible, overflow } = splitNavLinks(links, visibleCount);
  const overflowActive = overflow.some((l) => l.section === activeSection);

  function renderPill(link: NavEntry): ReactNode {
    const Icon = link.icon;
    const isActive = activeSection === link.section;
    return (
      <Link
        key={link.section}
        to={`/t/${tenantSlug}${link.suffix}`}
        aria-current={isActive ? 'page' : undefined}
        data-testid={`nav-${link.section}`}
        className={pillClass(isActive)}
      >
        <Icon size={17} aria-hidden="true" />
        {link.label}
      </Link>
    );
  }

  return (
    <nav
      ref={navRef}
      aria-label="Primary"
      data-testid="primary-nav"
      className="relative flex min-w-0 flex-1 items-center gap-1 ml-2"
    >
      {/* Hidden natural-width row — measured, never shown, out of flow so it
          never pushes the real row. Renders every item + a sample More trigger
          so the split math sees true widths. Spans (not links), aria-hidden +
          inert: excluded from the accessibility tree AND interaction, so
          role-based queries (AT, tests) can never resolve here. Text-based
          locators still see this copy — e2e specs must use role queries or
          content-specific text, never bare nav-label substrings (PR #529
          "Director"/"Directory" collision). */}
      {/* The row sits in a zero-size clipping box, so its natural width
          never reaches the page's scroll area and a nav wider than the
          screen never scrolls the page sideways. Inside the box the row is
          w-max and every pill shrink-0, so each still measures at its
          natural width. The nav itself does not clip: the More menu is
          never cut off. */}
      <div className="pointer-events-none absolute left-0 top-0 h-0 w-0 overflow-hidden">
        <div
          ref={measureRef}
          aria-hidden="true"
          inert
          data-testid="nav-measure"
          className="invisible flex w-max items-center gap-1"
        >
          {links.map((link) => {
            const Icon = link.icon;
            return (
              <span key={link.section} data-measure-item className={`shrink-0 ${pillClass(false)}`}>
                <Icon size={17} aria-hidden="true" />
                {link.label}
              </span>
            );
          })}
          <span ref={moreMeasureRef} data-measure-more className={`shrink-0 ${pillClass(false)}`}>
            <ChevronDown size={17} aria-hidden="true" />
            More
          </span>
        </div>
      </div>

      {/* The real, fitted row. */}
      {visible.map(renderPill)}

      {overflow.length > 0 && (
        <div ref={moreRef} className="relative">
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            aria-label="More navigation"
            data-testid="nav-more"
            className={pillClass(overflowActive)}
          >
            <ChevronDown size={17} aria-hidden="true" />
            More
          </button>

          {moreOpen && (
            <div
              role="menu"
              aria-label="More navigation"
              data-testid="nav-more-menu"
              className="absolute left-0 top-[46px] z-50 min-w-[220px] rounded-2xl border border-sage-800/[8%] bg-paper py-2 shadow-sh3"
            >
              {overflow.map((link) => {
                const Icon = link.icon;
                const isActive = activeSection === link.section;
                return (
                  <Link
                    key={link.section}
                    role="menuitem"
                    to={`/t/${tenantSlug}${link.suffix}`}
                    onClick={() => setMoreOpen(false)}
                    aria-current={isActive ? 'page' : undefined}
                    data-testid={`nav-more-${link.section}`}
                    className={
                      'flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors hover:bg-sand ' +
                      (isActive ? 'font-semibold text-sage-800' : 'text-ink')
                    }
                  >
                    <Icon size={17} aria-hidden="true" />
                    {link.label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
